import { useDl } from "../utils/dlToken";
import { useState, useEffect, useCallback, useRef } from "react";
import RevocationNotice from "../components/RevocationNotice";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import {
  ShieldCheck, LogOut, User, Building2, Mail, Lock, CheckCircle2, Home, Award,
  Clock, BadgeCheck, ArrowRight, FileText, Download, Search, Loader2, ArrowRightLeft,
  Trash2, AlertTriangle, X, Eye, EyeOff,
} from "lucide-react";
import PortalUploads from "../components/PortalUploads";
import SignDocuments from "../components/SignDocuments";
import SubmitApplication from "../components/SubmitApplication";
import MentorAudits from "../components/MentorAudits";
import MyProfile, { ChangePassword } from "../components/MyProfile";
import MyMentor from "../components/MyMentor";
import CandidateSearch from "../components/CandidateSearch";
import FinalFeeBox from "../components/FinalFeeBox";
import { TL } from "../content/talentI18n";

const PortalTabs = ({ tabs, tab, setTab, unread }) => (
  <div className="flex gap-1 border-b border-white/10 mb-8 overflow-x-auto" data-testid="portal-tabs">
    {tabs.map(([k, label]) => (
      <button key={k} onClick={() => setTab(k)} data-testid={`portal-tab-${k}`}
        className={`relative px-4 py-3 text-sm whitespace-nowrap transition-colors border-b-2 -mb-px ${tab === k ? "border-[#D4AF37] text-white" : "border-transparent text-white/50 hover:text-white"}`}>
        {label}
        {k === "profile" && unread > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4AF37] text-black text-[10px] font-bold" data-testid="portal-unread">{unread}</span>}
      </button>
    ))}
  </div>
);
import { PT } from "../content/portalI18n";
import { TERMS_CONSENT } from "../content/termsContent";
import ProfessionPicker from "../components/ProfessionPicker";
import { openPdf } from "../components/../utils/pdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "saubereko_applicant_token";

const COUNTRIES = [
  ["SK", "Slovensko"], ["CZ", "Česko"], ["AT", "Rakúsko"], ["DE", "Nemecko"],
  ["PL", "Poľsko"], ["HU", "Maďarsko"], ["FR", "Francúzsko"], ["IT", "Taliansko"],
  ["ES", "Španielsko"], ["NL", "Holandsko"], ["BE", "Belgicko"], ["IE", "Írsko"],
  ["RO", "Rumunsko"], ["BG", "Bulharsko"], ["HR", "Chorvátsko"], ["SI", "Slovinsko"],
  ["PT", "Portugalsko"], ["SE", "Švédsko"], ["DK", "Dánsko"], ["FI", "Fínsko"],
  ["GR", "Grécko"], ["LU", "Luxembursko"], ["LT", "Litva"], ["LV", "Lotyšsko"],
  ["EE", "Estónsko"], ["CY", "Cyprus"], ["MT", "Malta"],
  ["XX", "Iná krajina (mimo EÚ)"],
];

const fmtErr = (detail) => {
  if (detail == null) return "Nastala chyba. Skúste to znova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const Field = ({ label, ...props }) => (
  <div>
    <label className="block text-xs tracking-[0.18em] uppercase text-white/40 mb-2">{label}</label>
    <input
      {...props}
      autoCapitalize="none"
      autoCorrect="off"
      className="w-full bg-transparent border-b border-white/20 py-2.5 text-white focus:outline-none focus:border-[#D4AF37] transition-colors"
    />
  </div>
);

const PasswordField = ({ label, ...props }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-xs tracking-[0.18em] uppercase text-white/40 mb-2">{label}</label>
      <div className="relative">
        <input
          {...props}
          type={show ? "text" : "password"}
          autoCapitalize="none"
          autoCorrect="off"
          className="w-full bg-transparent border-b border-white/20 py-2.5 pr-10 text-white focus:outline-none focus:border-[#D4AF37] transition-colors"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          data-testid="toggle-password"
          aria-label={show ? "Skryť heslo" : "Zobraziť heslo"}
          className="absolute right-0 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#D4AF37] transition-colors"
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );
};

const Area = ({ label, ...props }) => (
  <div>
    <label className="block text-xs tracking-[0.18em] uppercase text-white/40 mb-2">{label}</label>
    <textarea
      {...props}
      rows={3}
      className="w-full bg-[#0d0d0d] border border-white/15 p-3 text-white text-sm focus:outline-none focus:border-[#D4AF37] transition-colors resize-y"
    />
  </div>
);

const Checkbox = ({ checked, onChange, children, testid }) => (
  <label className="flex items-start gap-3 cursor-pointer group">
    <input type="checkbox" checked={checked} onChange={onChange} data-testid={testid} className="mt-1 accent-[#D4AF37] w-4 h-4 shrink-0" />
    <span className="text-white/65 text-sm leading-relaxed group-hover:text-white/85 transition-colors">{children}</span>
  </label>
);

export default function Portal() {
  const { lang } = useLang();
  const t = PT[lang] || PT.sk;
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [mode, setMode] = useState("login"); // login | register
  const [me, setMe] = useState(null);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [twoFA, setTwoFA] = useState(null); // { challenge } when admin needs PIN
  const [pin, setPin] = useState("");

  // auth form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [type, setType] = useState("individual");
  const [consent, setConsent] = useState(false);

  const auth = (extra = {}) => ({ headers: { Authorization: `Bearer ${token}`, ...extra } });

  const fetchMe = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/applicant/me`, auth());
      setMe(data.applicant);
      setApplication(data.application);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      setMe(null);
    }
  }, [token]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const submitAuth = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const url = mode === "register" ? "/applicant/register" : "/applicant/login";
      const body = mode === "register"
        ? { name: name.trim(), email: email.trim(), password: password.trim(), type, consent }
        : { email: email.trim(), password: password.trim() };
      const { data } = await axios.post(`${API}${url}`, body);
      if (data.admin_2fa_required) {
        setTwoFA({ challenge: data.challenge });
        setPassword("");
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setMe(data.applicant);
      window.dispatchEvent(new Event("saubereko-auth"));
      setPassword("");
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail) || t.loginError);
    } finally { setLoading(false); }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/verify-2fa`, { challenge: twoFA.challenge, pin: pin.trim() });
      localStorage.setItem("saubereko_admin_token", data.token);
      window.location.href = "/riadiace-centrum";
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail) || "Overenie zlyhalo.");
    } finally { setLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setMe(null); setApplication(null);
    window.dispatchEvent(new Event("saubereko-auth"));
  };

  if (twoFA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white px-6 py-16" data-testid="admin-2fa-screen">
        <div className="grain" />
        <div className="relative w-full max-w-md">
          <Link to="/" className="block text-center mb-8 text-[#D4AF37] font-display text-2xl tracking-wide">SauberEko®</Link>
          <div className="border border-white/10 bg-[#0a0a0a] p-8 md:p-10">
            <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-b from-[#F2E3C6] to-[#D4AF37] text-black mb-6">
              <Lock size={22} />
            </div>
            <h1 className="font-display text-2xl mb-1">Bezpečnostné overenie</h1>
            <p className="text-white/50 text-sm mb-7">Zadajte bezpečnostný kód pre prístup do riadiaceho centra.</p>
            {error && <div className="mb-5 text-sm text-red-300 border border-red-400/30 bg-red-500/10 px-4 py-2.5" data-testid="admin-2fa-error">{error}</div>}
            <form onSubmit={submit2FA} className="space-y-6">
              <Field
                label="Bezpečnostný kód"
                type="password"
                inputMode="numeric"
                autoFocus
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                data-testid="admin-2fa-pin-input"
              />
              <button
                type="submit"
                disabled={loading || !pin.trim()}
                data-testid="admin-2fa-submit"
                className="w-full bg-gradient-to-r from-[#F2E3C6] to-[#D4AF37] text-black font-semibold py-3 tracking-wide hover:opacity-90 transition-opacity disabled:opacity-40"
              >
                {loading ? "Overujem…" : "Overiť a vstúpiť"}
              </button>
              <button
                type="button"
                onClick={() => { setTwoFA(null); setPin(""); setError(""); }}
                data-testid="admin-2fa-cancel"
                className="w-full text-white/40 text-sm hover:text-white/70 transition-colors"
              >
                Späť na prihlásenie
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white px-6 py-16">
        <div className="grain" />
        <div className="relative w-full max-w-md">
          <Link to="/" className="block text-center mb-8 text-[#D4AF37] font-display text-2xl tracking-wide">SauberEko®</Link>
          <div className="border border-white/10 bg-[#0a0a0a] p-8 md:p-10">
            <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-b from-[#F2E3C6] to-[#D4AF37] text-black mb-6">
              <ShieldCheck size={22} />
            </div>
            <h1 className="font-display text-2xl mb-1">{mode === "register" ? t.register : t.login}</h1>
            <p className="text-white/40 text-sm mb-7">{t.brand}</p>

            <div className="flex gap-1 mb-7 border border-white/10 p-1">
              <button onClick={() => setMode("login")} data-testid="tab-login"
                className={`flex-1 py-2 text-sm transition-colors ${mode === "login" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>{t.tabLogin}</button>
              <button onClick={() => setMode("register")} data-testid="tab-register"
                className={`flex-1 py-2 text-sm transition-colors ${mode === "register" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>{t.tabRegister}</button>
            </div>

            <form onSubmit={submitAuth} className="space-y-5" data-testid="portal-auth-form">
              {mode === "register" && (
                <>
                  <Field label={t.nameLabel} value={name} onChange={(e) => setName(e.target.value)} data-testid="reg-name" required />
                  <div>
                    <label className="block text-xs tracking-[0.18em] uppercase text-white/40 mb-2">{t.typeLabel}</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button type="button" onClick={() => setType("individual")} data-testid="type-individual"
                        className={`flex flex-col items-center gap-1 justify-center py-3 border text-sm transition-colors ${type === "individual" ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/15 text-white/55"}`}>
                        <span className="flex items-center gap-2"><User size={16} /> {t.individual}</span>
                        <span className="text-[11px] text-white/40">{t.individualHint}</span>
                      </button>
                      <button type="button" onClick={() => setType("company")} data-testid="type-company"
                        className={`flex flex-col items-center gap-1 justify-center py-3 border text-sm transition-colors ${type === "company" ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/15 text-white/55"}`}>
                        <span className="flex items-center gap-2"><Building2 size={16} /> {t.company}</span>
                        <span className="text-[11px] text-white/40">{t.companyHint}</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
              <Field label={t.email} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email" required />
              <PasswordField label={t.password} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-password" required />

              {mode === "register" && (
                <Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} testid="reg-consent">
                  {t.consentPre}<Link to="/privacy" className="text-[#D4AF37] underline">{t.privacy}</Link>)
                </Checkbox>
              )}

              {error && <p data-testid="auth-error" className="text-red-400 text-sm">{error}</p>}
              {mode === "login" && <p className="text-white/40 text-xs leading-relaxed" data-testid="hof-login-hint">{(TL[lang] || TL.sk).hofLogin}</p>}

              <button type="submit" disabled={loading || (mode === "register" && !consent)} data-testid="auth-submit"
                className="w-full bg-[#D4AF37] text-black font-semibold py-3.5 hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">
                {loading ? t.working : mode === "register" ? t.createAccount : t.enter}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return <Dashboard t={t} lang={lang} me={me} application={application} auth={auth} reload={fetchMe} logout={logout} token={token} />;
}

function Dashboard({ t, lang, me, application, auth, reload, logout, token }) {
  const dl = useDl(token);
  const approved = application?.status === "approved" || application?.status === "paid";
  const paid = application?.paid || application?.status === "paid";
  const isCompany = me.type === "company";
  const disc = application?.fee_discount > 0 && !isCompany;
  const feeStr = disc ? Number(application.fee_amount).toFixed(2).replace(".", ",") : "";
  const feeAmountLabel = isCompany ? t.feeAmountCompany : t.feeAmount;
  const payBtnLabel = isCompany ? t.payBtnCompany : disc ? t.payBtn.replace(/59/, feeStr) : t.payBtn;
  const [payState, setPayState] = useState(""); // "", processing, success, error
  const [payMsg, setPayMsg] = useState("");
  const [allSigned, setAllSigned] = useState(false);
  const formRef = useRef(null);
  const TLx = TL[lang] || TL.sk;
  const tabs = isCompany
    ? [["app", TLx.tabApp], ["search", TLx.tabSearch], ["account", TLx.tabAccount]]
    : [["app", TLx.tabApp], ["mentor", TLx.tabMentor], ["profile", TLx.tabProfile], ...(me.is_mentor ? [["mentorpanel", TLx.tabMentorPanel]] : [])];
  const [tab, setTabState] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("tab");
    return tabs.some(([k]) => k === q) ? q : (!isCompany && me.must_change_password ? "profile" : "app");
  });
  const setTab = (k) => { setTabState(k); window.history.replaceState({}, "", k === "app" ? "/ziadost" : `/ziadost?tab=${k}`); };
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (isCompany) return;
    axios.get(`${API}/applicant/profile`, auth()).then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle return from Stripe (?session_id=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid || params.get("tab") === "search" || params.get("lvl")) return;
    let attempts = 0;
    setPayState("processing");
    setPayMsg("Overujem platbu...");
    const poll = async () => {
      if (attempts >= 6) { setPayState("error"); setPayMsg("Platbu sa nepodarilo overiť. Skontrolujte e-mail alebo skúste znova."); return; }
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/applicant/payment/status/${sid}`, auth());
        if (data.payment_status === "paid") {
          setPayState("success"); setPayMsg(t.paySuccess);
          window.history.replaceState({}, "", "/ziadost");
          reload();
          return;
        }
        if (data.status === "expired") { setPayState("error"); setPayMsg(t.payExpired); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPayment = async () => {
    setPayState("processing"); setPayMsg(t.redirecting);
    try {
      const { data } = await axios.post(`${API}/applicant/payment/checkout`, { origin_url: window.location.origin }, auth());
      window.location.href = data.url;
    } catch (err) {
      setPayState("error"); setPayMsg(fmtErr(err.response?.data?.detail));
    }
  };

  const downloadInvoice = async () => {
    try {
      const res = await axios.get(`${API}/applicant/invoice/pdf`, { ...auth(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { alert(t.invoiceErr); }
  };

  const [switchBusy, setSwitchBusy] = useState(false);
  const [switchMsg, setSwitchMsg] = useState("");
  const typeLocked = approved || paid;
  const switchType = async () => {
    if (typeLocked) { setSwitchMsg(t.switchTypeLocked); return; }
    if (!window.confirm(t.switchTypeConfirm)) return;
    setSwitchBusy(true); setSwitchMsg("");
    try {
      const target = isCompany ? "individual" : "company";
      await axios.post(`${API}/applicant/switch-type`, { type: target }, auth());
      setSwitchMsg(t.switchTypeDone);
      reload();
    } catch (err) {
      setSwitchMsg(fmtErr(err.response?.data?.detail) || t.switchTypeErr);
    } finally { setSwitchBusy(false); }
  };

  const [myDocs, setMyDocs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/applicant/documents`, auth())
      .then(({ data }) => setMyDocs(data)).catch(() => setMyDocs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="portal-dashboard">
      <div className="grain" />
      <header className="relative border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 min-w-0">
            {me.type === "company" ? <Building2 className="text-[#D4AF37] shrink-0" size={22} /> : <User className="text-[#D4AF37] shrink-0" size={22} />}
            <div className="min-w-0">
              <p className="font-display text-lg leading-none truncate">{me.name}</p>
              <p className="text-white/40 text-xs mt-1 truncate">{me.type === "company" ? t.company : t.individual} · {me.email}</p>
            </div>
          </Link>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/"
              data-testid="portal-home"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <Home size={15} /> <span className="hidden sm:inline">{({ sk: "Domov", en: "Home", de: "Startseite", pl: "Strona główna" }[lang] || "Domov")}</span>
            </Link>
            <button onClick={logout} data-testid="portal-logout"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm hover:border-red-400 hover:text-red-400 transition-colors">
              <LogOut size={15} /> {t.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="relative max-w-3xl mx-auto px-6 py-10">
        <RevocationNotice lang={lang} auth={auth} />
        <PortalTabs tabs={tabs} tab={tab} setTab={setTab} unread={unread} />

        {tab === "search" && <CandidateSearch lang={lang} auth={auth} />}
        {tab === "mentor" && <MyMentor lang={lang} auth={auth} />}
        {tab === "mentorpanel" && <MentorAudits token={token} />}
        {tab === "profile" && (
          <MyProfile lang={lang} auth={auth} onUnread={setUnread}>
            <ChangeEmail currentEmail={me.email} auth={auth} reload={reload} />
            <DeleteAccount t={t} auth={auth} onDeleted={logout} />
          </MyProfile>
        )}
        {tab === "account" && (
          <>
            <ChangePassword L={TL[lang] || TL.sk} auth={auth} />
            <ChangeEmail currentEmail={me.email} auth={auth} reload={reload} />
            <DeleteAccount t={t} auth={auth} onDeleted={logout} />
          </>
        )}

        {tab === "app" && (<>
        <StatusBar application={application} allSigned={allSigned} />

        <div className="border border-white/10 bg-[#0a0a0a] p-6 mb-8" data-testid="portal-switch-type">
          <div className="flex items-center gap-2 mb-1">
            <ArrowRightLeft size={18} className="text-[#D4AF37]" />
            <h3 className="font-display text-xl">{t.switchTypeTitle}</h3>
          </div>
          <p className="text-white/40 text-sm mb-4">{t.switchTypeHint}</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-white/70" data-testid="switch-type-current">
              {isCompany ? <Building2 size={16} className="text-[#D4AF37]" /> : <User size={16} className="text-[#D4AF37]" />}
              <span>{t.switchTypeCurrent} <strong className="text-white">{isCompany ? t.company : t.individual}</strong></span>
            </div>
            <button
              onClick={switchType}
              disabled={switchBusy || typeLocked}
              data-testid="switch-type-button"
              className="inline-flex items-center justify-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-2.5 text-sm hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed sm:ml-auto"
            >
              {switchBusy ? <Loader2 size={15} className="animate-spin" /> : <ArrowRightLeft size={15} />}
              {isCompany ? t.switchToIndividual : t.switchToCompany}
            </button>
          </div>
          {typeLocked && <p className="text-white/35 text-xs mt-3" data-testid="switch-type-locked">{t.switchTypeLocked}</p>}
          {switchMsg && !typeLocked && <p className="text-[#D4AF37] text-sm mt-3" data-testid="switch-type-msg">{switchMsg}</p>}
        </div>

        <div className="border border-[#D4AF37]/25 bg-gradient-to-b from-[#D4AF37]/[0.06] to-transparent p-6 mb-8" data-testid="portal-howto">
          <h3 className="font-display text-xl mb-4 flex items-center gap-2">
            <BadgeCheck size={18} className="text-[#D4AF37]" /> {t.howToTitle}
          </h3>
          <ol className="space-y-3">
            {(t.howToSteps || []).map((step, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`howto-step-${i + 1}`}>
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#D4AF37] text-black text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                <span className="text-white/75 text-sm leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {myDocs.length > 0 && (
          <div className="border border-[#D4AF37]/30 bg-[#0a0a0a] p-6 mb-8" data-testid="portal-received-documents">
            <div className="flex items-center gap-2 mb-1">
              <BadgeCheck size={18} className="text-[#D4AF37]" />
              <h3 className="font-display text-xl">{t.myDocsTitle || "Moje dokumenty od značky profesionality a odbornosti"}</h3>
            </div>
            <p className="text-white/40 text-sm mb-5">{t.myDocsHint || "Oficiálne podpísané dokumenty, ktoré Vám zaslala značka profesionality a odbornosti SauberEko®."}</p>
            <div className="space-y-2">
              {myDocs.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-3 border border-white/10 px-4 py-3" data-testid={`my-doc-${d.id}`}>
                  <div className="min-w-0">
                    <p className="text-white/85 text-sm truncate">{d.title}</p>
                    <p className="text-white/35 text-xs mt-0.5">{(d.sent_at || "").slice(0, 10)}</p>
                  </div>
                  <button onClick={() => openPdf(`${API}/applicant/documents/${d.id}/download?token=${dl}`, { filename: `${d.type}-saubereko.pdf` })}
                    data-testid={`my-doc-download-${d.id}`}
                    className="inline-flex items-center gap-1.5 text-[#D4AF37] text-xs font-semibold shrink-0 hover:underline">
                    <Download size={14} /> {t.docDownload || "Stiahnuť"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {approved && !paid && (
          <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6 mb-8" data-testid="payment-section">
            <div className="flex items-start gap-3 mb-4">
              <BadgeCheck size={22} className="text-[#D4AF37] shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display text-xl">{t.feeTitle}</h3>
                <p className="text-white/60 text-sm mt-1">{t.feeDesc1}<strong className={disc ? "text-white/40 line-through" : "text-[#D4AF37]"}>{feeAmountLabel}</strong>{t.feeDesc2}</p>
                {disc && (
                  <p className="text-[#F2E3C6] text-sm mt-2" data-testid="fee-discount-info">
                    {({ sk: "Zľava", en: "Discount", de: "Rabatt", pl: "Rabat", fr: "Remise" })[lang] || "Zľava"} {application.fee_discount} % → <strong className="text-[#D4AF37]">{feeStr} €</strong>
                  </p>
                )}
              </div>
            </div>
            {payState === "error" && <p data-testid="payment-error" className="text-red-400 text-sm mb-3">{payMsg}</p>}
            <button onClick={startPayment} disabled={payState === "processing"} data-testid="pay-button"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-7 py-3.5 hover:bg-[#F2E3C6] transition-colors disabled:opacity-60">
              {payState === "processing" ? payMsg || t.working : payBtnLabel}
              <ArrowRight size={18} />
            </button>
          </div>
        )}

        {payState === "success" && (
          <div className="border border-green-500/40 bg-green-500/5 p-5 mb-8 flex items-center gap-3" data-testid="payment-success">
            <CheckCircle2 size={20} className="text-green-400 shrink-0" />
            <p className="text-white/80 text-sm">{payMsg}</p>
          </div>
        )}

        {paid && application?.fee_waived && (
          <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5 mb-8 flex items-center gap-3" data-testid="fee-waived-card">
            <BadgeCheck size={20} className="text-[#D4AF37] shrink-0" />
            <p className="text-white/80 text-sm">{({ sk: "Registračný poplatok Vám bol odpustený. Nemusíte nič uhrádzať, overovanie začína.", en: "Your registration fee has been waived. No payment is required, the verification is starting.", de: "Ihre Registrierungsgebühr wurde erlassen. Es ist keine Zahlung nötig, die Prüfung beginnt.", pl: "Opłata rejestracyjna została Państwu odpuszczona. Nie trzeba nic płacić, weryfikacja się rozpoczyna.", fr: "Vos frais d'inscription ont été annulés. Aucun paiement n'est requis, la vérification commence." })[lang] || "Registračný poplatok Vám bol odpustený."}</p>
          </div>
        )}
        {paid && !application?.fee_waived && (
          <div className="border border-green-500/30 bg-green-500/5 p-5 mb-8 flex items-center justify-between gap-3 flex-wrap" data-testid="invoice-download-card">
            <div className="flex items-center gap-3">
              <BadgeCheck size={20} className="text-green-400 shrink-0" />
              <p className="text-white/80 text-sm">{t.paidNote}</p>
            </div>
            <button onClick={downloadInvoice} data-testid="download-invoice-btn"
              className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-sm font-semibold hover:bg-[#D4AF37] hover:text-black transition-colors">
              <FileText size={16} /> {t.downloadInvoice}
            </button>
          </div>
        )}

        <FinalFeeBox application={application} auth={auth} lang={lang} />
        <ApplicationForm t={t} key={application?.id || "new"} me={me} application={application} auth={auth} reload={reload} approved={approved} formRef={formRef} />

        <div className="mt-8">
          <SignDocuments t={t} token={token} hasApplication={Boolean(application)} onSignedChange={setAllSigned} />
        </div>

        {application && <PortalUploads t={t} token={token} application={application} reload={reload} />}

        <SubmitApplication t={t} token={token} allSigned={allSigned} formRef={approved ? null : formRef} alreadySubmitted={!!me?.submission_completed_at} />
        </>)}
      </main>
    </div>
  );
}

function ChangeEmail({ currentEmail, auth, reload }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await axios.post(`${API}/applicant/change-email`, { email: email.trim() }, auth());
      setMsg({ type: "ok", text: "E-mailová adresa bola zmenená." });
      setOpen(false); setEmail("");
      reload();
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.detail || "Zmena zlyhala." });
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-6 mt-10" data-testid="portal-change-email">
      <div className="flex items-center gap-2 mb-1">
        <Mail size={18} className="text-[#D4AF37]" />
        <h3 className="font-display text-xl text-white">E-mailová adresa</h3>
      </div>
      <p className="text-white/45 text-sm mb-4">Súčasná adresa: <span className="text-white/80">{currentEmail}</span></p>
      {msg && (
        <div data-testid="change-email-msg" className={`text-sm px-3 py-2 mb-3 border ${msg.type === "ok" ? "border-[#D4AF37]/40 text-[#F2E3C6] bg-[#D4AF37]/5" : "border-red-500/40 text-red-300 bg-red-500/5"}`}>{msg.text}</div>
      )}
      {!open ? (
        <button onClick={() => { setOpen(true); setEmail(""); setMsg(null); }} data-testid="change-email-open"
          className="inline-flex items-center gap-2 border border-white/15 text-white/70 px-4 py-2 text-sm hover:border-[#D4AF37]/50 hover:text-white transition-colors">
          <Mail size={15} /> Zmeniť e-mail
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="change-email-input"
            placeholder="nový@email.sk"
            className="flex-1 min-w-[240px] bg-black border border-white/15 px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none" />
          <button onClick={save} disabled={busy || !email.trim()} data-testid="change-email-save"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-4 py-2 text-sm hover:bg-[#e5c14f] transition-colors disabled:opacity-40">
            {busy ? <Loader2 size={15} className="animate-spin" /> : "Uložiť"}
          </button>
          <button onClick={() => { setOpen(false); setMsg(null); }} data-testid="change-email-cancel"
            className="text-white/40 hover:text-white text-sm px-2">Zrušiť</button>
        </div>
      )}
    </div>
  );
}

function DeleteAccount({ t, auth, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [confirmWord, setConfirmWord] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const canDelete = confirmWord.trim().toUpperCase() === (t.deleteConfirmWord || "ZMAZAŤ").toUpperCase();

  const doDelete = async () => {
    if (!canDelete) return;
    setBusy(true); setError("");
    try {
      await axios.delete(`${API}/applicant/account`, auth());
      alert(t.deleteDone);
      onDeleted();
    } catch {
      setError(t.deleteErr);
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-red-500/25 bg-red-500/[0.04] p-6 mt-10" data-testid="portal-delete-account">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle size={18} className="text-red-400" />
        <h3 className="font-display text-xl text-white">{t.dangerTitle}</h3>
      </div>
      <p className="text-white/45 text-sm mb-5 leading-relaxed max-w-2xl">{t.dangerHint}</p>
      <button onClick={() => { setOpen(true); setConfirmWord(""); setError(""); }} data-testid="delete-account-open"
        className="inline-flex items-center gap-2 border border-red-500/50 text-red-300 px-5 py-2.5 text-sm hover:bg-red-500/10 transition-colors">
        <Trash2 size={15} /> {t.deleteBtn}
      </button>

      {open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/75 backdrop-blur-sm px-4" data-testid="delete-account-modal">
          <div className="w-full max-w-md bg-[#0b0b0b] border border-red-500/30 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="font-display text-lg text-white flex items-center gap-2"><AlertTriangle size={18} className="text-red-400" /> {t.deleteConfirmTitle}</h3>
              <button onClick={() => setOpen(false)} data-testid="delete-account-close" className="text-white/40 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-white/60 text-sm leading-relaxed">{t.deleteConfirmText} <strong className="text-red-300">{t.deleteConfirmWord}</strong>.</p>
              <input
                value={confirmWord}
                onChange={(e) => setConfirmWord(e.target.value)}
                placeholder={t.deleteTypePlaceholder}
                data-testid="delete-account-input"
                autoFocus
                className="w-full bg-[#111] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-red-400 transition-colors"
              />
              {error && <p className="text-red-400 text-sm" data-testid="delete-account-error">{error}</p>}
              <div className="flex items-center gap-2 pt-1">
                <button onClick={() => setOpen(false)} data-testid="delete-account-cancel"
                  className="border border-white/15 px-5 py-2.5 text-sm text-white/70 hover:text-white transition-colors">{t.deleteCancel}</button>
                <button onClick={doDelete} disabled={!canDelete || busy} data-testid="delete-account-confirm"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-red-600 text-white font-semibold py-2.5 px-4 hover:bg-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  {busy ? <><Loader2 size={16} className="animate-spin" /> …</> : <><Trash2 size={16} /> {t.deleteConfirmBtn}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const STEP_T = {
  sk: ["Vyplnená žiadosť", "Podpísané dokumenty", "Odoslaná žiadosť", "Potvrdenie o prijatí", "Schválená + úhrada 59 €", "Prebieha overovanie", "Overené – doklad / certifikát"],
  en: ["Application filled in", "Documents signed", "Application sent", "Receipt confirmed", "Approved + €59 fee", "Verification in progress", "Verified – document issued"],
  de: ["Antrag ausgefüllt", "Dokumente unterschrieben", "Antrag gesendet", "Eingang bestätigt", "Genehmigt + 59 € Gebühr", "Prüfung läuft", "Geprüft – Dokument ausgestellt"],
  pl: ["Wniosek wypełniony", "Dokumenty podpisane", "Wniosek wysłany", "Potwierdzenie przyjęcia", "Zatwierdzony + opłata 59 €", "Trwa weryfikacja", "Zweryfikowano – dokument wydany"],
};

function StatusBar({ application, allSigned }) {
  const { lang } = useLang();
  const L = [...(STEP_T[lang] || STEP_T.sk)];
  if (application?.fee_waived) L[4] = ({ sk: "Schválená – poplatok odpustený", en: "Approved – fee waived", de: "Genehmigt – Gebühr erlassen", pl: "Zatwierdzony – opłata zniesiona" })[lang] || "Schválená – poplatok odpustený";
  else if (application?.fee_discount > 0) L[4] = L[4].replace(/59/, Number(application.fee_amount).toFixed(2).replace(".", ","));
  else if (application?.type === "company") L[4] = L[4].replace(/59/, "490");
  const st = application?.status;
  const sent = !!st && st !== "draft";
  const approved = ["approved", "paid"].includes(st) || !!application?.paid;
  const paid = st === "paid" || !!application?.paid;
  const done = [!!application, allSigned || sent, sent, sent, approved && paid, paid && !!application?.verification_success_at,
    !!(application?.verification_success_at || application?.certified || application?.hof_inducted)];
  const current = done.findIndex((d) => !d);
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5 mb-8" data-testid="portal-status">
      <ol className="grid grid-cols-1 sm:grid-cols-7 gap-3 sm:gap-2">
        {L.map((label, i) => (
          <li key={label} className="flex sm:flex-col items-center sm:text-center gap-3 sm:gap-2" data-testid={`portal-step-${i + 1}`}>
            <span className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${done[i] ? "bg-[#D4AF37] text-black" : i === current ? "border-2 border-[#D4AF37] text-[#D4AF37]" : "bg-white/5 text-white/30 border border-white/10"}`}>
              {done[i] ? <CheckCircle2 size={17} /> : i + 1}
            </span>
            <span className={`text-xs leading-tight ${done[i] ? "text-white" : i === current ? "text-[#F2E3C6]" : "text-white/35"}`}>{label}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}


function TrackChoice({ value, onChange, disabled, locked }) {
  const { lang } = useLang();
  const T = TRACK_T[lang] || TRACK_T.sk;
  const opts = [["certificate", T.cert, T.certD, T.certReq], ["verification", T.ver, T.verD, T.verReq]].filter(([k]) => !locked || k === "verification");
  return (
    <section data-testid="app-track">
      <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-3">{T.title}</p>
      <div className={`grid gap-3 ${locked ? "" : "sm:grid-cols-2"}`}>
        {opts.map(([k, l, d, req]) => (
          <button key={k} type="button" disabled={disabled} onClick={() => onChange(k)} data-testid={`app-track-${k}`}
            className={`text-left p-4 border transition-colors disabled:opacity-60 ${k === "certificate" ? "bg-gradient-to-b from-[#D4AF37]/[0.07] to-transparent" : ""} ${value === k ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/15 hover:border-[#D4AF37]/50"}`}>
            <p className={`font-display text-lg ${k === "certificate" ? "text-[#F2E3C6]" : "text-white"}`}>{l}</p>
            <p className="text-white/50 text-xs mt-1 leading-relaxed">{d}</p>
            <p className={`text-[11px] mt-2 leading-relaxed ${k === "certificate" ? "text-[#D4AF37]" : "text-white/40"}`} data-testid={`app-track-req-${k}`}>{req}</p>
          </button>
        ))}
      </div>
      {!locked && <p className="text-[#F2E3C6]/80 text-xs mt-3" data-testid="app-track-diff">{T.diff}</p>}
      {!locked && <p className="text-white/35 text-xs mt-2">{T.fee}</p>}
    </section>
  );
}

const TRACK_T = {
  sk: { diff: "Zlatý certifikát a preverenie sú dve úplne odlišné úrovne. Zlatý certifikát je najvyššie ocenenie značky SauberEko® – len pre skúsených profesionálov.", certReq: "Nárok: aktívna odborná prax v odbore minimálne 5 rokov, formálna kontrola podkladov, odborný audit v praxi a záverečná osobná kontrola mentorom.", verReq: "Pre každého, kto chce mať overené zručnosti – aj začínajúcich. Doklad o preverení nie je certifikát.", title: "O čo žiadate", cert: "Zlatý certifikát Sa.Eko.Prs.", certD: "Plná certifikácia profesionála s číslom Sa.Eko.Prs. a zápisom v Sieni slávy.", ver: "Preverenie online Sa.Eko.Verified", verD: "Preverenie online na základe zaslaných podkladov (fotky, videá, dokumentácia), rovnako ako pri certifikácii. Trvá 1 až 3 mesiace podľa Vašej pripravenosti.", fee: "Registračný poplatok 59 € (vrátane DPH) platí pre obe možnosti." },
  en: { diff: "The gold certificate and verification are two completely different levels. The gold certificate is the highest SauberEko® distinction – only for experienced professionals.", certReq: "Eligibility: at least 5 years of active professional practice, document check, practical expert audit and a final in-person check by a mentor.", verReq: "For anyone who wants verified skills – including beginners. A verification document is not a certificate.", title: "What are you applying for", cert: "Gold certificate Sa.Eko.Prs.", certD: "Full professional certification with a Sa.Eko.Prs. number and a Hall of Fame listing.", ver: "Online verification Sa.Eko.Verified", verD: "Online verification based on submitted evidence (photos, videos, documentation), just like for certification. Takes 1 to 3 months depending on your readiness.", fee: "Registration fee €59 (VAT incl.) applies to both options." },
  de: { diff: "Das goldene Zertifikat und die Prüfung sind zwei völlig unterschiedliche Stufen. Das goldene Zertifikat ist die höchste Auszeichnung von SauberEko® – nur für erfahrene Fachkräfte.", certReq: "Voraussetzung: mindestens 5 Jahre aktive Berufspraxis, Unterlagenprüfung, fachliches Audit in der Praxis und abschließende persönliche Kontrolle durch einen Mentor.", verReq: "Für alle, die geprüfte Fähigkeiten wollen – auch Einsteiger. Ein Prüfnachweis ist kein Zertifikat.", title: "Wofür beantragen Sie", cert: "Goldenes Zertifikat Sa.Eko.Prs.", certD: "Vollständige Zertifizierung mit Sa.Eko.Prs.-Nummer und Eintrag in der Hall of Fame.", ver: "Online-Prüfung Sa.Eko.Verified", verD: "Online-Prüfung auf Grundlage eingereichter Nachweise (Fotos, Videos, Dokumentation), wie bei der Zertifizierung. Dauer 1 bis 3 Monate je nach Ihrer Vorbereitung.", fee: "Registrierungsgebühr 59 € (inkl. MwSt.) für beide Optionen." },
  pl: { diff: "Złoty certyfikat i weryfikacja to dwa zupełnie różne poziomy. Złoty certyfikat to najwyższe wyróżnienie SauberEko® – tylko dla doświadczonych fachowców.", certReq: "Warunek: co najmniej 5 lat aktywnej praktyki zawodowej, kontrola dokumentów, audyt w praktyce i końcowa osobista kontrola mentora.", verReq: "Dla każdego, kto chce mieć zweryfikowane umiejętności – także początkujących. Dokument weryfikacji nie jest certyfikatem.", title: "O co wnioskujesz", cert: "Złoty certyfikat Sa.Eko.Prs.", certD: "Pełna certyfikacja z numerem Sa.Eko.Prs. i wpisem w Galerii Sław.", ver: "Weryfikacja online Sa.Eko.Verified", verD: "Weryfikacja online na podstawie przesłanych materiałów (zdjęcia, filmy, dokumentacja), tak jak przy certyfikacji. Trwa od 1 do 3 miesięcy w zależności od przygotowania.", fee: "Opłata rejestracyjna 59 € (z VAT) dla obu opcji." },
};

function ApplicationForm({ t, me, application, auth, reload, approved, formRef }) {
  const { lang } = useLang();
  const lockVer = me.type !== "company" && !me.submission_completed_at && !approved
    && (new URLSearchParams(window.location.search).get("track") === "verification" || localStorage.getItem("saubereko_pref_track") === "verification");
  useEffect(() => { if (me.submission_completed_at) localStorage.removeItem("saubereko_pref_track"); }, [me.submission_completed_at]);
  const [f, setF] = useState({
    subject_name: application?.subject_name || me.name || "",
    ico_birth: application?.ico_birth || "",
    ico: application?.ico || "",
    address: application?.address || "",
    country: application?.country || "SK",
    vat_id: application?.vat_id || "",
    responsible_person: application?.responsible_person || "",
    phone: application?.phone || "",
    specialization: application?.specialization || "",
    professions: application?.professions || [],
    prof_levels: application?.prof_levels || {},
    profession_other: application?.profession_other || "",
    years_practice: application?.years_practice ?? null,
    workers_count: application?.workers_count || "",
    equipment: application?.equipment || "",
    certificates: application?.certificates || "",
    decl_audit: application?.decl_audit || false,
    decl_pillars: application?.decl_pillars || false,
    decl_understanding: application?.decl_understanding || false,
    kodex_consent: application?.kodex_consent || false,
    terms_consent: application?.terms_consent || false,
    track: lockVer ? "verification" : application?.track || me.track || "certificate",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupMsg, setLookupMsg] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const toggle = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.checked }));

  const lookupCompany = async () => {
    const ico = (f.ico_birth || "").replace(/\D/g, "");
    if (ico.length !== 8) { setLookupMsg(t.icoLookupHint || "Zadajte 8-miestne IČO."); return; }
    setLookupBusy(true); setLookupMsg("");
    try {
      const { data } = await axios.get(`${API}/company/lookup?ico=${ico}`);
      if (!data.found) { setLookupMsg(t.icoLookupNone || "Firma sa nenašla."); return; }
      const addr = [data.street, [data.zip, data.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
      setF((p) => ({ ...p, subject_name: data.name || p.subject_name, ico_birth: data.ico || p.ico_birth, address: addr || p.address }));
      setLookupMsg(`✓ ${data.name}${data.dic ? " · DIČ " + data.dic : ""}`);
    } catch (e) {
      setLookupMsg(e.response?.data?.detail || "Register nedostupný.");
    } finally { setLookupBusy(false); }
  };

  const goldTooFew = me.type !== "company" && f.track === "certificate" && f.years_practice != null && f.years_practice < 5;
  const profOk = f.professions.length > 0 && f.professions.every((p) => (f.prof_levels || {})[p]) && f.years_practice != null && (!f.professions.includes("other") || f.profession_other.trim());
  const allDecl = f.decl_audit && f.decl_pillars && f.decl_understanding && f.kodex_consent && f.terms_consent && profOk && !goldTooFew;
  useEffect(() => { if (formRef) formRef.current = { f, ok: allDecl }; }, [f, allDecl, formRef]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setMsg(""); setSaving(true);
    try {
      await axios.post(`${API}/applicant/application`, f, auth());
      setMsg(application ? t.appSavedUpd : t.appSavedNew);
      reload();
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="application-form">
      <div>
        <h2 className="font-display text-2xl mb-1">{t.appTitle}</h2>
        <p className="text-white/40 text-sm">{t.appSubtitle}</p>
      </div>

      {me.type !== "company" && <TrackChoice value={f.track} onChange={(v) => setF((p) => ({ ...p, track: v }))} disabled={approved} locked={lockVer} />}

      <ProfessionPicker lang={lang} f={f} setF={setF} disabled={approved || !!me?.submission_completed_at} goldTooFew={goldTooFew} />

      <section className="space-y-5">
        <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase">{t.sec1}</p>
        <Field label={me.type === "company" ? t.fName : t.fNameInd} value={f.subject_name} onChange={set("subject_name")} data-testid="app-subject-name" required disabled={approved} />
        <div className="grid md:grid-cols-2 gap-5">
          <Field label={me.type === "company" ? t.fIco : t.fBirth} value={f.ico_birth} onChange={set("ico_birth")} data-testid="app-ico" disabled={approved} />
          <Field label={t.fPhone} value={f.phone} onChange={set("phone")} data-testid="app-phone" disabled={approved} />
        </div>
        {me.type !== "company" && (
          <Field label={t.fIcoInd} value={f.ico} onChange={set("ico")} data-testid="app-ico-individual" disabled={approved} placeholder="napr. 12 345 678" />
        )}
        {me.type === "company" && !approved && (
          <div>
            <button type="button" onClick={lookupCompany} disabled={lookupBusy} data-testid="app-ico-lookup"
              className="inline-flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] px-3 py-1.5 text-xs hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50">
              {lookupBusy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} {t.icoLookup || "Vyhľadať firmu podľa IČO"}
            </button>
            {lookupMsg && <span className="text-[11px] ml-3 text-white/50" data-testid="app-lookup-msg">{lookupMsg}</span>}
          </div>
        )}
        <Field label={me.type === "company" ? t.fSeat : t.fAddr} value={f.address} onChange={set("address")} data-testid="app-address" disabled={approved} />
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs tracking-[0.18em] uppercase text-white/40 mb-2">{t.fCountry}</label>
            <select value={f.country} onChange={set("country")} data-testid="app-country" disabled={approved}
              className="w-full bg-[#0d0d0d] border-b border-white/20 py-2.5 text-white focus:outline-none focus:border-[#D4AF37] transition-colors disabled:opacity-50">
              {COUNTRIES.map(([code, name]) => <option key={code} value={code} className="bg-[#0d0d0d]">{name}</option>)}
            </select>
          </div>
          <Field label={t.fVatId} value={f.vat_id} onChange={set("vat_id")} data-testid="app-vatid" disabled={approved} placeholder="napr. CZ12345678" />
        </div>
        {f.country !== "SK" && (
          <p className="text-white/40 text-xs leading-relaxed">
            {f.country === "XX" ? t.regimeNonEu : f.vat_id ? t.regimeRc : t.regimeOss}
          </p>
        )}
        <Field label={t.fResponsible} value={f.responsible_person} onChange={set("responsible_person")} data-testid="app-responsible" disabled={approved} />
      </section>

      <section className="space-y-5">
        <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase">{t.sec2}</p>
        <Area label={t.fSpec} value={f.specialization} onChange={set("specialization")} data-testid="app-specialization" required disabled={approved} />
      </section>

      <section className="space-y-5">
        <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase">{t.sec3}</p>
        <Field label={t.fWorkers} value={f.workers_count} onChange={set("workers_count")} data-testid="app-workers" disabled={approved} />
        <Area label={t.fEquip} value={f.equipment} onChange={set("equipment")} data-testid="app-equipment" disabled={approved} />
        <Area label={t.fCerts} value={f.certificates} onChange={set("certificates")} data-testid="app-certificates" disabled={approved} />
      </section>

      <section className="space-y-4 border border-white/10 bg-[#0a0a0a] p-6" data-testid="portal-documents">
        <div className="flex items-center gap-2 mb-1">
          <FileText size={18} className="text-[#D4AF37]" />
          <h3 className="font-display text-xl">{t.docsTitle}</h3>
        </div>
        <p className="text-white/40 text-sm mb-2">{t.docsHint}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { label: t.docZiadost, file: "/ziadost-certifikacia-saubereko.pdf", tid: "doc-ziadost" },
            { label: t.docKodex, file: "/kodex-saubereko-2026.pdf", tid: "doc-kodex" },
            { label: t.docSuhlas, file: "/suhlas-spracovanie-udajov-saubereko.pdf", tid: "doc-suhlas" },
          ].map((d) => (
            <a key={d.tid} href={d.file} download target="_blank" rel="noreferrer" data-testid={d.tid}
              className="group flex items-center justify-between gap-3 border border-white/10 px-4 py-3 hover:border-[#D4AF37]/50 transition-colors">
              <span className="text-white/85 text-sm">{d.label}</span>
              <span className="inline-flex items-center gap-1.5 text-[#D4AF37] text-xs font-semibold shrink-0">
                <Download size={14} /> {t.docDownload}
              </span>
            </a>
          ))}
        </div>
      </section>

      <section className="space-y-4 border border-white/10 bg-[#0a0a0a] p-6">
        <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase">{t.sec4}</p>
        <Checkbox checked={f.kodex_consent} onChange={toggle("kodex_consent")} testid="decl-kodex">{t.declKodex}</Checkbox>
        <Checkbox checked={f.decl_audit} onChange={toggle("decl_audit")} testid="decl-audit">{t.declAudit}</Checkbox>
        <Checkbox checked={f.decl_pillars} onChange={toggle("decl_pillars")} testid="decl-pillars">{t.declPillars}</Checkbox>
        <Checkbox checked={f.decl_understanding} onChange={toggle("decl_understanding")} testid="decl-understanding">{t.declUnderstanding}</Checkbox>
        <Checkbox checked={f.terms_consent} onChange={toggle("terms_consent")} testid="decl-terms">
          {(TERMS_CONSENT[lang] || TERMS_CONSENT.sk)[0]}<a href="/obchodne-podmienky" target="_blank" rel="noreferrer" data-testid="decl-terms-link" className="text-[#D4AF37] underline underline-offset-2">{(TERMS_CONSENT[lang] || TERMS_CONSENT.sk)[1]}</a>{(TERMS_CONSENT[lang] || TERMS_CONSENT.sk)[2]}
        </Checkbox>
      </section>

      {msg && !application && <p data-testid="app-success" className="text-[#D4AF37] text-sm">{msg}</p>}
      {application && !approved && !me?.submission_completed_at && (
        <p data-testid="app-saved-pending" className="text-[#D4AF37] text-sm border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-3">{t.savedPending}</p>
      )}
      {error && <p data-testid="app-error" className="text-red-400 text-sm">{error}</p>}

      {!approved && !application && (
        <button type="submit" disabled={saving || !allDecl} data-testid="app-submit"
          className="group inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-8 py-3.5 hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">
          {saving ? t.sending : t.submitNew}
          <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
        </button>
      )}
      {approved && (
        <div className="flex items-center gap-3 border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5">
          <Clock size={20} className="text-[#D4AF37] shrink-0" />
          <p className="text-white/75 text-sm">{t.approvedMsg}</p>
        </div>
      )}
    </form>
  );
}
